import { Box, styled, useMediaQuery } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";

import { CheckoutInputV2 } from "@/components/Checkout/components/CheckoutInputV2";
import ButtonContainer from "@/components/Checkout/components/CheckoutSummary/components/Buttons/ButtonContainer";
import ClearButton from "@/components/Checkout/components/CheckoutSummary/components/Buttons/ClearButton";
import SaveButton from "@/components/Checkout/components/CheckoutSummary/components/Buttons/SaveButton";
import { ResponsiveDialog } from "@/components/StoreComponents/StoreDialogs/ResponsiveDialog";
import { Retailer } from "@/redux/types";
import {
  getEntityImage,
  MOBILE_SCREEN_SIZE,
  TABLET_SCREEN_SIZE,
} from "@brandclub/common-ui";
import LeavingConfirm from "./LeavingConfirm";

enum PopupStatus {
  OTP = "otp",
  LEAVING = "leaving",
}

const AccountInputFormContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  // width: "100%",
  padding: "24px",
  height: "100%",
  gap: "32px",
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {},
  [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    gap: "24px",
  },
  ".input-container": {
    display: "flex",
    flexDirection: "row",
    gap: "16px",
    width: "100%",
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {},
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      flexDirection: "column",
      gap: "15px",
    },
  },
}));

const Container = styled(motion.div)(({ theme }) => ({
  position: "relative",
  width: "100%",
  overflow: "hidden",
  display: "flex",
  color: theme.palette.primary.main,
}));

export const OTPPopup = ({
  open: showEdit,
  onClose: closeEdit,
  value: otp,
  onChange: setOtp,
  onSave: onSaveCredentials,
  retailer,
  onResend,
  resendCoolDown,
}: {
  open: boolean;
  onClose: () => void;
  value?: string;
  onChange: (otp: string) => void;
  onSave: () => void;
  retailer?: Retailer;
  onResend: () => void;
  resendCoolDown: number;
}) => {
  const [status, setStatus] = useState<PopupStatus>(PopupStatus.OTP);
  const [previousStatus, setPreviousStatus] = useState<PopupStatus>(
    PopupStatus.OTP
  );

  const onCloseEdit = () => {
    setStatus(PopupStatus.OTP);
    closeEdit();
  };

  const mobile = useMediaQuery(`(max-width: ${MOBILE_SCREEN_SIZE}px)`);

  const ResponsiveResendText = mobile ? "Resend code" : "Resend";

  const ResendText =
    resendCoolDown > 0 ? `Resend in ${resendCoolDown}s` : ResponsiveResendText;

  const getDialogBody = () => {
    switch (status) {
      case PopupStatus.OTP:
        return (
          <AccountInputFormContainer>
            {/* Retailer Icon */}
            <Box
              sx={{
                bgcolor: "#e6f3f7",
                borderRadius: "50%",
                width: 72,
                height: 72,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <img
                className="retailer_logo"
                alt="r_logo"
                style={{
                  width: "72px",
                  height: "72px",
                  objectFit: "contain",
                }}
                src={getEntityImage(retailer?.retailerId!, "retailer")}
              />
            </Box>
            {/* Title */}
            <Box
              sx={(theme) => ({
                fontWeight: 700,
                fontSize: 22,
                [theme.breakpoints.down("xs")]: {
                  fontSize: 18,
                },
                textAlign: "center",
              })}
            >
              Verify your {retailer?.displayName ?? "retailer"} account
            </Box>
            <Box>
              Enter your {retailer?.displayName ?? "retailer"} credentials to
              connect your account to Brandclub.
            </Box>
            <div className={"input-container"}>
              <CheckoutInputV2
                label={"Verification code"}
                onChange={(e) => setOtp(e.target.value)}
                value={otp}
              />
            </div>
            <ButtonContainer>
              <ClearButton disabled={resendCoolDown > 0} onClick={onResend}>
                {ResendText}
              </ClearButton>
              <SaveButton onClick={onSaveCredentials} disabled={!otp}>
                Submit
              </SaveButton>
            </ButtonContainer>{" "}
          </AccountInputFormContainer>
        );
      case PopupStatus.LEAVING:
        return (
          <Box sx={{ width: "100%", height: "100%" }}>
            <LeavingConfirm
              closeEdit={onCloseEdit}
              goBack={() => {
                setStatus(previousStatus);
              }}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <ResponsiveDialog
      open={showEdit}
      onClose={
        status === PopupStatus.LEAVING
          ? undefined
          : () => {
              setPreviousStatus(status);
              setStatus(PopupStatus.LEAVING);
            }
      }
    >
      <Container
        sx={(theme) => ({
          [theme.breakpoints.up("md")]: {
            minHeight: 600,
            maxHeight: 600,
            minWidth: 450,
            maxWidth: 450,
          },
          overflowY: "auto",
          maxWidth: "100%",
        })}
      >
        <AnimatePresence mode="wait">
          <motion.div
            className="retailer-connect-login-motion-container"
            style={{
              boxSizing: "border-box",
              width: "100%",
              height: "100%",
              display: "flex",
              padding: "24px 24px",
              flexDirection: "column",
            }}
            key={status}
            animate={{ opacity: 1, x: 0 }}
            initial={{ opacity: 0, x: 20 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.15 }}
          >
            {getDialogBody()}
          </motion.div>
        </AnimatePresence>
      </Container>
    </ResponsiveDialog>
  );
};
